<template>
    <div class="advanced-input-container">
        <div
            v-click-outside="() => showToolbar = false"
            data-qa="advanced-input"
            class="advanced-input"
            :class="{ 'error-state': isInvalid }"
            @click.capture="() => showToolbar = true"
        >
            <div class="input-container">
                <input
                    v-if="mode === 'input'"
                    ref="textBox"
                    v-model="localValue"
                    tabindex="-1"
                    data-qa="advanced-input-textbox"
                    class="text-box"
                    :autocomplete="autocomplete"
                    :required="isRequired"
                    :maxlength="textBoxMaxLength"
                />

                <textarea
                    v-else
                    ref="textBox"
                    v-model="localValue"
                    tabindex="-1"
                    data-qa="advanced-input-textbox"
                    class="text-box"
                    :rows="textareaRows"
                    :autocomplete="autocomplete"
                    :required="isRequired"
                    :maxlength="textBoxMaxLength"
                    @input="resizeTextarea"
                />

                <label
                    data-qa="advanced-input-label"
                    :class="{ 'has-content': hasContent }"
                >
                    {{ label }}
                </label>
            </div>

            <!-- The click event listener on this div is used to keep the input/textarea
            in focus. If you do not want this to happen, prevent the click event from
            bubbling. We could use @click.capture instead of @click but there are
            use cases where we would not want the input/textarea to be focused when we
            click on an element in the slot. Example: If another input element is present
            in the slot then using @click.capture on this div will prevent the
            input element in the slot from being focused when we click on it -->
            <div
                data-qa="advanced-input-toolbar"
                class="input-toolbar"
                :class="{ 'open': showToolbar || alwaysShowToolbar }"
                @click="handleToolbarClicked"
            >
                <slot name="toolbar" :insert-text-into-input="insertTextIntoInput" />
            </div>
        </div>

        <p
            v-if="errorText && submitted"
            data-qa="advanced-input-error"
            class="error-text"
        >
            {{ errorText }}
        </p>

        <p
            v-if="helpText"
            data-qa="advanced-input-help-text"
            class="help-text"
        >
            {{ helpText }}
        </p>
    </div>
</template>

<script>
import debounce from 'lodash.debounce';

const RESIZE_TEXTAREA_DELAY_MS = 200;
const INPUT_MAXLENGTH = 232;
const TEXTAREA_MAXLENGTH = 2000;

export default {
    name: 'AdvancedInput',

    props: {
        autocomplete: {
            type: String,
            default: 'off',
        },
        errorText: String,
        helpText: String,
        invalid: Boolean,
        label: {
            type: String,
            default: '',
        },
        maxlength: Number,
        required: Boolean,
        value: {
            type: String,
            default: '',
        },
        submitted: Boolean,
        mode: {
            type: String,
            validator: (value) => ['input', 'textarea'].includes(value),
            default: 'input',
        },
        autoResizeTextarea: Boolean,
        textareaRows: {
            type: Number,
            default: 3,
        },
        alwaysShowToolbar: Boolean,
    },

    data() {
        return {
            localValue: '',
            showToolbar: false,
            inputHasFocus: false,
            textBoxMaxLength: 0,
        };
    },

    created() {
        // `value` can be null sometimes. Even though the default value for this prop
        // is an empty string, vue does not convert null to the default value
        this.localValue = this.value ?? '';

        if (this.maxlength >= 0) {
            this.textBoxMaxLength = this.maxlength;
        } else {
            this.textBoxMaxLength = (this.mode === 'input') ? INPUT_MAXLENGTH : TEXTAREA_MAXLENGTH;
        }

        this.resizeTextarea = debounce(this.resizeTextarea, RESIZE_TEXTAREA_DELAY_MS);
    },

    mounted() {
        if (this.mode === 'textarea' && this.autoResizeTextarea) {
            this.resizeTextarea();
        }
    },

    watch: {
        localValue(val) {
            if (val !== this.value) {
                this.$emit('input', val);
            }
        },

        value(val) {
            this.localValue = val;
        },
    },

    computed: {
        isInvalid() {
            return this.invalid || (this.required && this.localValue.length === 0 && this.submitted);
        },

        hasContent() {
            return this.localValue.trim() !== '';
        },

        isRequired() {
            // vue does not render attribute if it's value is null; compatible with v2 and v3
            return this.required ? true : null;
        },
    },

    methods: {
        async resizeTextarea() {
            if (!this.autoResizeTextarea) {
                return;
            }

            await this.$nextTick();

            const { textBox } = this.$refs;

            textBox.style.height = 'auto';
            textBox.style.height = `${textBox.scrollHeight}px`;
        },

        async handleToolbarClicked() {
            const { textBox } = this.$refs;

            await this.$nextTick();

            const { selectionStart, selectionEnd } = textBox;

            textBox.setSelectionRange(selectionStart, selectionEnd);

            textBox.focus();
        },

        async insertTextIntoInput(newText = '') {
            const { textBox } = this.$refs;
            const { selectionStart, selectionEnd } = textBox;
            const currentValue = this.localValue || '';

            const textBeforeSelection = currentValue.substring(0, selectionStart);
            const textAfterSelection = currentValue.substring(selectionEnd);

            this.localValue = textBeforeSelection + newText + textAfterSelection;

            const newPosition = selectionStart + newText.length;

            await this.$nextTick();

            textBox.setSelectionRange(newPosition, newPosition);
            textBox.focus();
        },
    },
};
</script>

<style lang="scss" scoped>
$transition-duration: 100ms;
$toolbar-height: var(--toolbar-height, 40px);

@mixin label-to-top {
    @include input-label-selected;
    background: linear-gradient(to top, transparent, $color-white 40%);
}

.input-toolbar {
    --emoji-picker-top: #{$toolbar-height};

    position: relative;
    z-index: 2;
    transform: scaleY(0);
    display: flex;
    flex-direction: row;
    height: 0;
    visibility: hidden; /* to make contents of input-toolbar not focusable when toolbar is not open */
    transition: transform $transition-duration, height $transition-duration;
    background-color: $color-ink-050;
}

.input-container {
    position: relative;

    .text-box {
        border: none;
        outline: none;
        resize: none;
        background-color: transparent;
        max-height: px-to-rem(200);
        width: 100%;
        padding: $spacing-125 $spacing-200;
        height: auto;

        &[required] ~ label::after {
            content: '*';
            color: $color-red;
        }
    }
}

.advanced-input-container {
    margin-bottom: $spacing-200;
}

.advanced-input {
    position: relative;
    display: flex;
    flex-direction: column;
    flex: 1;
    background: $color-white;
    border: 1px solid $color-gray-400;
    border-radius: 0.5rem;
    transition: border 250ms cubic-bezier(0.23, 1, 0.32, 1);

    label {
        @include label;

        padding: 0;

        &.has-content {
            @include label-to-top;
        }
    }

    &:focus-within {
        border-color: $color-blue;

        label {
            @include label-to-top;
            color: $color-blue;
        }
    }

    &.error-state {
        border-color: $color-red;

        label {
            color: $color-red;
        }
    }

    .input-toolbar.open {
        visibility: visible;
        height: #{$toolbar-height};
        transform: scaleY(1);
    }
}

.error-text,
.help-text {
    font-size: 0.75rem;
}

.error-text {
    color: $color-red;
    margin-bottom: 0;
}
</style>
